import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import SEO from "../../../utils/seo";
import Button from "../../../components/base/Button";
import { Helmet } from "react-helmet";

const HillsideServiceArea = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Starlight Motors Mobile Detailing - Hillside",
    "description": "Professional mobile car detailing service in Hillside, VIC. Full interior & exterior detailing, ceramic coating, and paint correction delivered to your location.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hillside",
      "addressRegion": "VIC",
      "postalCode": "3037",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -37.6897,
      "longitude": 144.7439
    },
    "telephone": "0491121950",
    "priceRange": "$170-$600",
    "areaServed": {
      "@type": "City",
      "name": "Hillside, VIC"
    }
  };

  const services = [
    {
      title: "Full Interior & Exterior Detail",
      price: "$250",
      originalPrice: "$280",
      features: ["Complete exterior wash & wax", "Interior deep clean", "Leather conditioning", "Window cleaning", "Tire shine"]
    },
    {
      title: "Interior Only Detail",
      price: "$180",
      originalPrice: "$210",
      features: ["Deep vacuum", "Leather treatment", "Dashboard cleaning", "Seat protection", "Interior windows"]
    },
    {
      title: "Exterior Only Detail",
      price: "$120",
      originalPrice: "$150",
      features: ["Exterior wash", "Paint protection wax", "Wheel cleaning", "Tire shine", "Trim restoration"]
    },
    {
      title: "Ceramic Coating",
      price: "from $600",
      features: ["Paint correction", "Premium coating", "2-5 year protection", "Enhanced gloss", "Easy maintenance"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mobile Car Detailing Hillside | Starlight Motors - Professional Service</title>
        <meta name="description" content="Premium mobile car detailing in Hillside, Melbourne. Starlight Motors brings professional detailing to your door. Interior & exterior detailing, ceramic coating. Call 0491121950 for Hillside's trusted mobile detailing service." />
        <meta name="keywords" content="mobile car detailing Hillside, car detailing Hillside Melbourne, ceramic coating Hillside, mobile auto detailing" />
        <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/hillside`} />
        <meta property="og:title" content="Mobile Car Detailing Hillside | Starlight Motors" />
        <meta property="og:description" content="Premium mobile car detailing in Hillside, Melbourne. Professional service at your location." />
        <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/hillside`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PTMBTSXR');`}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="relative max-w-7xl mx-auto px-6">
              <div className="max-w-3xl">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                  <strong>Mobile Car Detailing in Hillside</strong>
                </h1>
                <p className="text-xl md:text-2xl text-gray-200 mb-8">
                  Professional mobile car detailing services delivered to your Hillside driveway. Showroom-quality results without leaving home.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="tel:0491121950"
                    className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2 text-xl"></i>
                    Call 0491 121 950
                  </a>
                  <a
                    href="/#contact"
                    className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Free Quote
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    <strong>Why Choose Starlight Motors in Hillside?</strong>
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    As Hillside's trusted mobile car detailing specialists, Starlight Motors brings professional-grade equipment and premium products directly to your location. Whether you're in the Hillside Shopping Centre area or residential streets, we provide convenient, high-quality car detailing services that save you time and deliver exceptional results.
                  </p>
                  <p className="text-lg text-gray-700 mb-6">
                    Our mobile car detailing service in Hillside includes everything from basic maintenance washes to premium ceramic coating applications. We understand the needs of Hillside residents and offer flexible scheduling to fit your busy lifestyle.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-start">
                      <i className="ri-check-line text-blue-600 text-2xl mr-3 flex-shrink-0"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1"><strong>Local Hillside Service</strong></h4>
                        <p className="text-sm text-gray-600">Trusted by Hillside residents for 3+ years</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-check-line text-blue-600 text-2xl mr-3 flex-shrink-0"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1"><strong>Premium Products</strong></h4>
                        <p className="text-sm text-gray-600">Professional-grade detailing supplies</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-check-line text-blue-600 text-2xl mr-3 flex-shrink-0"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1"><strong>Fully Mobile</strong></h4>
                        <p className="text-sm text-gray-600">We come to your Hillside location</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-check-line text-blue-600 text-2xl mr-3 flex-shrink-0"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1"><strong>Satisfaction Guaranteed</strong></h4>
                        <p className="text-sm text-gray-600">100% quality guarantee on all services</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/0e8ab525c9b97d2f11dedad76e9a15b4.jpg"
                    alt="Mobile car detailing service in Hillside"
                    title="Professional car detailing in Hillside - Starlight Motors"
                    className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
                  />
                </div>
              </div>

              <div className="mb-16">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                  <strong>Our Hillside Car Detailing Services</strong>
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {services.map((service, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-600 hover:shadow-lg transition-all">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-lg font-bold text-gray-900"><strong>{service.title}</strong></h4>
                        <div className="text-right ml-2">
                          {service.originalPrice && (
                            <div className="text-xs text-gray-400 line-through">{service.originalPrice}</div>
                          )}
                          <span className="text-blue-600 font-bold text-lg whitespace-nowrap">{service.price}</span>
                        </div>
                      </div>
                      {service.originalPrice && (
                        <div className="mb-3 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                          Save $30!
                        </div>
                      )}
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0 mt-0.5"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12">
                <div className="max-w-3xl mx-auto text-center">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    <strong>Serving All of Hillside, VIC 3037</strong>
                  </h3>
                  <p className="text-lg text-gray-700 mb-6">
                    We provide mobile car detailing services throughout Hillside, including residential areas near Hillside Shopping Centre, Hillside Boulevard, and all surrounding streets. Our mobile service means we bring everything needed for professional car detailing directly to your Hillside home or workplace.
                  </p>
                  <p className="text-lg text-gray-700 mb-8">
                    Whether you need a quick maintenance detail, full interior and exterior detailing, or premium ceramic coating protection, Starlight Motors delivers exceptional results at your Hillside location. Book your mobile car detailing service today and experience the convenience of professional car care without leaving home.
                  </p>
                  <a 
                    href="/#contact"
                    className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Book Hillside Car Detailing
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default HillsideServiceArea;
